import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AnimatePresence, motion } from "framer-motion";
import { format, differenceInCalendarDays, isPast, parseISO } from "date-fns";
import {
  Sparkles,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Calendar as CalendarIcon,
  Repeat,
  Undo2,
  Maximize2,
  Pencil,
  Share2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import TaskTimer from "./TaskTimer";
import SubtaskItem from "./SubtaskItem";

const priorityClasses = {
  high: "border-red-300 text-red-700 bg-red-50",
  medium: "border-amber-300 text-amber-700 bg-amber-50",
  low: "border-blue-300 text-blue-700 bg-blue-50",
};

const categoryClasses = {
  work: "border-zinc-300 text-zinc-700 bg-zinc-50",
  personal: "border-violet-300 text-violet-700 bg-violet-50",
};

function DueBadge({ due }) {
  if (!due) return null;
  let date;
  try {
    date = typeof due === "string" ? parseISO(due) : due;
  } catch {
    return null;
  }
  const days = differenceInCalendarDays(date, new Date());
  const overdue = isPast(date) && days < 0;
  const dueToday = days === 0;
  const cls = overdue ? "text-red-700" : dueToday ? "text-amber-700" : "text-zinc-500";
  const label = overdue
    ? `Overdue · ${format(date, "MMM d")}`
    : dueToday
    ? `Due today`
    : `Due ${format(date, "MMM d")}`;
  return (
    <span className={`flex items-center gap-1 text-xs ${cls}`}>
      <CalendarIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
      {label}
    </span>
  );
}

export default function TaskItem({
  task,
  isRemoving,
  isCompletedView = false,
  onToggle,
  onDelete,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
  onReorderSubtasks,
  onBreakdown,
  onUpdate,
  onFocus,
  onEdit,
  onShare,
  readOnly = false,
}) {
  const [expanded, setExpanded] = useState(true);
  const [addingSub, setAddingSub] = useState(false);
  const [subInput, setSubInput] = useState("");
  const [breaking, setBreaking] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const sortable = useSortable({ id: task.id, disabled: isCompletedView });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    background: isDragging ? "#fff" : "transparent",
    boxShadow: isDragging ? "0 10px 30px rgba(0,0,0,0.08)" : "none",
  };

  const subtasks = task.subtasks || [];
  const subDone = subtasks.filter((s) => s.completed).length;
  const progressPct = subtasks.length > 0 ? Math.round((subDone / subtasks.length) * 100) : 0;

  const subSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleSubDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderSubtasks) return;
    const ids = subtasks.map((s) => s.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(ids, oldIdx, newIdx);
    onReorderSubtasks(reordered);
  };

  const handleBreakdown = async () => {
    setBreaking(true);
    try {
      await onBreakdown();
      setExpanded(true);
    } finally {
      setBreaking(false);
    }
  };

  const submitSub = (e) => {
    e?.preventDefault();
    if (!subInput.trim()) return;
    onAddSubtask(subInput.trim());
    setSubInput("");
    setAddingSub(false);
  };

  const beginInlineEdit = () => {
    if (isCompletedView || task.completed) return;
    setTitleDraft(task.title);
    setEditingTitle(true);
  };

  const commitInlineEdit = () => {
    const next = titleDraft.trim();
    if (next && next !== task.title) {
      onUpdate({ title: next });
    }
    setEditingTitle(false);
  };

  const cancelInlineEdit = () => {
    setEditingTitle(false);
    setTitleDraft("");
  };

  // Tap-discoverable on mobile: visible by default, fade-in-on-hover only on md+
  const actionsCls =
    "flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity";

  return (
    <motion.li
      ref={setNodeRef}
      style={style}
      layout
      initial={{ opacity: 1, height: "auto" }}
      animate={
        isRemoving
          ? { opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }
          : { opacity: 1, height: "auto" }
      }
      transition={{ duration: 0.45, ease: "easeInOut" }}
      className="border-b border-zinc-200 group"
      data-testid={`task-item-${task.id}`}
    >
      <div className="flex items-start gap-2 sm:gap-3 px-2 py-4 hover:bg-zinc-50 transition-colors">
        {!isCompletedView && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="drag-handle pt-1 text-zinc-300 hover:text-zinc-600 hidden md:block md:opacity-0 md:group-hover:opacity-100 transition-opacity"
            aria-label="Drag to reorder"
            data-testid={`drag-handle-${task.id}`}
          >
            <GripVertical className="w-4 h-4" strokeWidth={1.5} />
          </button>
        )}

        <div className="pt-1">
          <Checkbox
            className="swiss-checkbox"
            checked={task.completed}
            onCheckedChange={onToggle}
            disabled={readOnly}
            data-testid={`task-checkbox-${task.id}`}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 sm:gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              {editingTitle ? (
                <input
                  type="text"
                  value={titleDraft}
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={commitInlineEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitInlineEdit();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      cancelInlineEdit();
                    }
                  }}
                  className="w-full text-base font-medium text-zinc-900 leading-snug bg-white border border-[#002FA7] outline-none px-2 py-0.5 -mx-2 -my-0.5"
                  data-testid={`inline-title-input-${task.id}`}
                />
              ) : (
                <div
                  className={`text-base font-medium text-zinc-900 leading-snug break-words ${
                    task.completed ? "line-through text-zinc-400" : ""
                  } ${
                    !isCompletedView && !task.completed
                      ? "cursor-text hover:bg-zinc-100/60 rounded-sm -mx-1 px-1"
                      : ""
                  }`}
                  onClick={beginInlineEdit}
                  title={!isCompletedView && !task.completed ? "Click to edit" : undefined}
                  data-testid={`task-title-${task.id}`}
                >
                  {task.title}
                </div>
              )}
              {task.description && (
                <div className="text-sm text-zinc-500 mt-1 leading-snug whitespace-pre-line line-clamp-3">
                  {task.description}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span
                  className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 border ${priorityClasses[task.priority]}`}
                  data-testid={`priority-badge-${task.id}`}
                >
                  {task.priority}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 border ${categoryClasses[task.category]}`}
                  data-testid={`category-badge-${task.id}`}
                >
                  {task.category}
                </span>
                {task.recurrence && task.recurrence !== "none" && (
                  <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-zinc-500">
                    <Repeat className="w-3 h-3" strokeWidth={1.5} />
                    {task.recurrence}
                  </span>
                )}
                <DueBadge due={task.due_date} />
                {!isCompletedView && task.duration_minutes ? (
                  <TaskTimer task={task} onPatch={onUpdate} />
                ) : null}
                {isCompletedView && task.completed_at && (
                  <span className="text-xs text-zinc-400">
                    Done {format(parseISO(task.completed_at), "MMM d, h:mm a")}
                  </span>
                )}
              </div>

              {subtasks.length > 0 && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    className="flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                    data-testid={`expand-subtasks-${task.id}`}
                  >
                    {expanded ? (
                      <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    )}
                    {subDone}/{subtasks.length} subtasks
                  </button>
                  <div className="flex-1 h-1 bg-zinc-100 max-w-[200px]">
                    <div
                      className="h-full bg-[#002FA7] transition-all duration-500"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={actionsCls}>
              {readOnly ? (
                <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 px-2 py-1 border border-zinc-200" data-testid={`task-readonly-${task.id}`}>
                  Read-only
                </span>
              ) : !isCompletedView ? (
                <>
                  <button
                    type="button"
                    onClick={onEdit}
                    className="flex items-center gap-1 text-xs px-2 py-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                    title="Edit"
                    data-testid={`edit-btn-${task.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                  <button
                    type="button"
                    onClick={onShare}
                    className={`flex items-center gap-1 text-xs px-2 py-1 transition-colors ${
                      task.share_token
                        ? "text-emerald-700 hover:bg-emerald-50"
                        : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                    }`}
                    title={task.share_token ? "Shared — manage link" : "Share read-only link"}
                    data-testid={`share-btn-${task.id}`}
                  >
                    <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="hidden sm:inline">{task.share_token ? "Shared" : "Share"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={onFocus}
                    className="flex items-center gap-1 text-xs px-2 py-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                    title="Focus mode"
                    data-testid={`focus-btn-${task.id}`}
                  >
                    <Maximize2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="hidden sm:inline">Focus</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleBreakdown}
                    disabled={breaking}
                    className="flex items-center gap-1 text-xs px-2 py-1 text-zinc-600 hover:text-[#002FA7] hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-colors disabled:opacity-50 whitespace-nowrap"
                    data-testid={`ai-breakdown-btn-${task.id}`}
                  >
                    <Sparkles
                      className={`w-3.5 h-3.5 ${breaking ? "animate-pulse" : ""}`}
                      strokeWidth={1.5}
                    />
                    <span className="hidden sm:inline">{breaking ? "Thinking..." : "Break down"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddingSub(true)}
                    className="flex items-center gap-1 text-xs px-2 py-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                    data-testid={`add-subtask-btn-${task.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="hidden sm:inline">Subtask</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={onToggle}
                  className="flex items-center gap-1 text-xs px-2 py-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                  data-testid={`restore-task-btn-${task.id}`}
                >
                  <Undo2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Restore</span>
                </button>
              )}
              <button
                type="button"
                onClick={onDelete}
                disabled={readOnly}
                className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Delete task"
                data-testid={`delete-task-btn-${task.id}`}
                style={{ display: readOnly ? "none" : undefined }}
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <AnimatePresence initial={false}>
            {expanded && (subtasks.length > 0 || addingSub) && (
              <motion.div
                key="subs"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden mt-3 ml-1 border-l border-zinc-200 pl-4"
                data-testid={`subtask-list-${task.id}`}
              >
                {isCompletedView ? (
                  <ul>
                    {subtasks.map((s) => (
                      <SubtaskItem
                        key={s.id}
                        subtask={s}
                        isCompletedView
                        onToggle={() => {}}
                        onDelete={() => {}}
                      />
                    ))}
                  </ul>
                ) : (
                  <DndContext
                    sensors={subSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleSubDragEnd}
                  >
                    <SortableContext
                      items={subtasks.map((s) => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <ul>
                        {subtasks.map((s) => (
                          <SubtaskItem
                            key={s.id}
                            subtask={s}
                            onToggle={(c) => onToggleSubtask(s.id, c)}
                            onDelete={() => onDeleteSubtask(s.id)}
                          />
                        ))}
                      </ul>
                    </SortableContext>
                  </DndContext>
                )}

                {addingSub && (
                  <form onSubmit={submitSub} className="flex items-center gap-2 py-2">
                    <input
                      autoFocus
                      type="text"
                      value={subInput}
                      onChange={(e) => setSubInput(e.target.value)}
                      onBlur={() => !subInput && setAddingSub(false)}
                      placeholder="Subtask title..."
                      className="flex-1 text-sm px-2 py-1 border border-zinc-300 focus:border-[#002FA7] outline-none"
                      data-testid={`subtask-input-${task.id}`}
                    />
                    <button
                      type="submit"
                      className="text-xs px-2 py-1 bg-zinc-900 text-white hover:bg-[#002FA7]"
                      data-testid={`submit-subtask-${task.id}`}
                    >
                      Add
                    </button>
                  </form>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.li>
  );
}
